/**
 * IndexedDB-backed storage for spreadsheet data.
 *
 * Each sheet's data is stored as a 2D array keyed by `${sessionId}_${sheetId}`.
 * The interface is deliberately simple so the backend can be swapped later
 * (e.g. to S3 or a server-side store) without changing consumers.
 */

const DB_NAME = 'analyst_sheets';
const DB_VERSION = 1;
const STORE_NAME = 'sheets';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function makeKey(sessionId, sheetId) {
  return `${sessionId}_${sheetId}`;
}

/**
 * Save a sheet's raw 2D data array to IndexedDB.
 */
export async function saveSheetData(sessionId, sheetId, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({
      key: makeKey(sessionId, sheetId),
      sessionId,
      sheetId,
      data,
      updatedAt: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Load a sheet's raw 2D data array from IndexedDB.
 * Returns null if no data is stored yet.
 */
export async function loadSheetData(sessionId, sheetId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(makeKey(sessionId, sheetId));
    request.onsuccess = () => resolve(request.result?.data ?? null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a single sheet's data from IndexedDB.
 */
export async function deleteSheetData(sessionId, sheetId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(makeKey(sessionId, sheetId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Delete all sheets belonging to a session from IndexedDB.
 */
export async function deleteSessionData(sessionId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.sessionId === sessionId) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
